import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

const ProgramsList = () => {
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.programs);
        setPrograms(response.data);
      } catch (err) {
        console.error('Failed to fetch programs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPrograms();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <Helmet><title>Nos Programmes - HPD</title></Helmet>
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-navy border-t-transparent mx-auto" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <Helmet>
        <title>Nos Programmes - Hope for Peace and Development</title>
        <meta name="description" content="Decouvrez les programmes de HPD pour l'autonomisation des femmes et de la communaute entiere." />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-navy overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Badge className="bg-forest text-white px-4 py-2 text-sm font-sans mb-4">{programs.length} Programmes d'Impact</Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white mb-4 tracking-tight">Nos Programmes</h1>
            <div className="w-16 h-1 bg-forest rounded-full mb-6" />
            <p className="text-lg text-white/70 max-w-3xl font-sans">Des actions concretes pour un changement durable en RDC</p>
          </motion.div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {programs.map((program, index) => (
              <motion.div
                key={program.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group cursor-pointer bg-white rounded-3xl overflow-hidden border border-black/5 shadow-sm hover:shadow-md transition-all duration-300"
                onClick={() => program.slug && navigate(`/programmes/${program.slug}`)}
                data-testid={`program-card-${index}`}
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={program.images?.[0] || 'https://images.unsplash.com/photo-1544928147-79a2dbc1f389'}
                    alt={program.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" />
                  {program.impact_stats && (
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-forest text-white text-xs px-3 py-1">{program.impact_stats.beneficiaries || '500+'} beneficiaires</Badge>
                    </div>
                  )}
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-serif font-bold text-navy mb-3 group-hover:text-forest transition-colors">{program.title}</h3>
                  <p className="text-gray-600 leading-relaxed text-sm font-sans mb-6">{program.short_description}</p>
                  <div className="flex items-center text-forest font-semibold text-sm group-hover:translate-x-2 transition-transform font-sans">
                    <span>En savoir plus</span>
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-warmgray">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy mb-6 tracking-tight">Soutenez Nos Programmes</h2>
          <p className="text-base md:text-lg text-gray-600 mb-8 font-sans max-w-2xl mx-auto">
            Chaque contribution fait une difference dans la vie de nos beneficiaires.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigate('/don')} className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-forest text-white font-semibold hover:bg-forest/90 transition-all shadow-lg group font-sans">
              Faire un Don <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => navigate('/about')} className="inline-flex items-center justify-center px-8 py-4 rounded-full border-2 border-navy text-navy font-semibold hover:bg-navy hover:text-white transition-all font-sans">
              En Savoir Plus sur HPD
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProgramsList;
